import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, InlineLoading, InlineNotification, Modal, Tag } from '@carbon/react';
import { useLinkEntrySchedule, useSchedules } from 'src/hooks/useSchedules';
import ShiftLabel from './ShiftLabel';
import { EntrySchedule, aroundDay, plannedFromApi, scheduleErrorKey } from './scheduleModel';

/** An entry's planned shift (and lateness), or "Unscheduled" (spec 2026-09-27 §7.3). */
export const EntryScheduleLabel = ({ info, timezone }: { info?: EntrySchedule; timezone: string }) => {
  const { t } = useTranslation();
  if (info?.link) {
    return (
      <div className="timesheet-detail__schedule">
        <Tag size="sm" type="teal" data-testid="entry-schedule-link">
          <ShiftLabel
            shiftName={info.link.shiftName}
            startAt={info.link.startAt}
            endAt={info.link.endAt}
            timezone={timezone}
            fallbackName={t('schedule.shift')}
          />
        </Tag>
        {info.link.late && (
          <Tag size="sm" type="warm-gray">
            {t('schedule.late')}
          </Tag>
        )}
      </div>
    );
  }
  if (info?.unscheduled) {
    return (
      <Tag size="sm" type="purple" data-testid="entry-schedule-unscheduled">
        {t('schedule.unscheduled')}
      </Tag>
    );
  }
  return null;
};

/**
 * Manual link (spec §4.1): the employee's own published seats around the entry's day that are
 * free or already this entry's. The server re-checks employee, restaurant and liveness.
 */
export const LinkShiftModal = ({
  timesheetId,
  employeeId,
  entryId,
  date,
  timezone,
  current,
  onClose,
}: {
  timesheetId: string;
  employeeId: string;
  entryId: string;
  date: string;
  timezone: string;
  current: string | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const around = aroundDay(date);
  const { data, isLoading, isSuccess, isError } = useSchedules(around.from, around.to);
  const link = useLinkEntrySchedule(timesheetId);
  const candidates = (data?.shifts ?? [])
    .map(plannedFromApi)
    .filter((shift) => shift.published)
    .flatMap((shift) =>
      shift.assignees
        .filter((a) => a.employeeId === employeeId && (a.timeEntryId === null || a.timeEntryId === entryId))
        .map((assignee) => ({ shift, assignee }))
    );
  const choose = (assignmentId: string | null) => link.mutate({ entryId, assignmentId }, { onSuccess: onClose });

  return (
    <Modal open passiveModal modalHeading={t('schedule.linkShift')} onRequestClose={onClose}>
      <div data-testid="link-shift-dialog" className="schedule-dialog">
        {isLoading && <InlineLoading description={t('common.loading')} />}
        {/* Loading and error states get their own copy — "no candidates" only once the query
            actually succeeded with none, so a slow or failed fetch never reads as "nobody's free". */}
        {isError && (
          <InlineNotification kind="error" lowContrast hideCloseButton title={t('schedule.errors.generic')} />
        )}
        {isSuccess && candidates.length === 0 && <p>{t('schedule.noCandidates')}</p>}
        {candidates.map(({ shift, assignee }) => (
          <Button
            key={assignee.assignmentId}
            kind={assignee.assignmentId === current ? 'primary' : 'tertiary'}
            size="sm"
            data-testid={`link-shift-${assignee.assignmentId}`}
            disabled={link.isLoading}
            onClick={() => choose(assignee.assignmentId)}
          >
            <span>{dayjs(shift.startAt).tz(timezone).format('DD/MM')}</span>
            <span aria-hidden> · </span>
            <ShiftLabel shiftName={shift.shiftName} startAt={shift.startAt} endAt={shift.endAt} timezone={timezone} />
          </Button>
        ))}
        {current && (
          <Button
            kind="danger--ghost"
            size="sm"
            data-testid="link-shift-unlink"
            disabled={link.isLoading}
            onClick={() => choose(null)}
          >
            {t('schedule.unlink')}
          </Button>
        )}
        {link.error ? (
          <InlineNotification kind="error" lowContrast hideCloseButton title={t(scheduleErrorKey(link.error))} />
        ) : null}
      </div>
    </Modal>
  );
};
